import { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useWorkoutHistory } from '@/hooks/useWorkoutHistory';
import { WorkoutDayModal } from '@/components/WorkoutDayModal';
import { SessionSummary } from '@/lib/types';

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function toDateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

type CalCell = { day: number; month: number; year: number; isCurr: boolean };

export function WorkoutCalendar({ studentId }: { studentId: string }) {
  const { historyMap } = useWorkoutHistory(studentId);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSessions, setSelectedSessions] = useState<SessionSummary[]>([]);

  const today = new Date();
  const todayKey = toDateKey(today.getFullYear(), today.getMonth(), today.getDate());

  const startDate = new Date(today);
  startDate.setDate(today.getDate() - today.getDay() - 7);

  const calCells: CalCell[] = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    return {
      day: d.getDate(),
      month: d.getMonth(),
      year: d.getFullYear(),
      isCurr: d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(),
    };
  });

  function handleDayPress(cell: CalCell) {
    const key = toDateKey(cell.year, cell.month, cell.day);
    const sessions = historyMap[key];
    if (!sessions?.length) return;
    setSelectedDate(key);
    setSelectedSessions(sessions);
  }

  return (
    <View style={styles.calCard}>
      <Text style={styles.calMonth}>{MONTHS_PT[today.getMonth()]} {today.getFullYear()}</Text>
      <View style={styles.calWeekRow}>
        {WEEKDAYS.map((d, i) => <Text key={i} style={styles.calWeekDay}>{d}</Text>)}
      </View>
      <View style={styles.calGrid}>
        {calCells.map((cell, i) => {
          const key = toDateKey(cell.year, cell.month, cell.day);
          const hasSessions = !!(historyMap[key]?.length);
          const isToday = key === todayKey;
          return (
            <TouchableOpacity
              key={i}
              style={styles.calCell}
              onPress={() => handleDayPress(cell)}
              activeOpacity={hasSessions ? 0.7 : 1}
            >
              <View style={[
                styles.calDayCircle,
                hasSessions && styles.calDayDone,
                isToday && !hasSessions && styles.calDayToday,
              ]}>
                <Text style={[
                  styles.calDayText,
                  !cell.isCurr && styles.calDayFaded,
                  hasSessions && styles.calDayTextDone,
                  isToday && !hasSessions && styles.calDayTextToday,
                ]}>{cell.day}</Text>
              </View>
            </TouchableOpacity>
          );
        })}
      </View>

      <WorkoutDayModal
        date={selectedDate}
        sessions={selectedSessions}
        visible={!!selectedDate}
        onClose={() => setSelectedDate(null)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  calCard: { backgroundColor: '#fff', borderRadius: 12, marginBottom: 10, borderWidth: 1.5, borderColor: '#eee', padding: 14 },
  calMonth: { fontSize: 13, fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 },
  calWeekRow: { flexDirection: 'row', marginBottom: 4 },
  calWeekDay: { flex: 1, textAlign: 'center', fontSize: 11, fontWeight: '600', color: '#ccc' },
  calGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  calCell: { width: '14.285714%', aspectRatio: 1, alignItems: 'center', justifyContent: 'center' },
  calDayCircle: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center' },
  calDayDone: { backgroundColor: '#C8102E' },
  calDayToday: { borderWidth: 1.5, borderColor: '#C8102E' },
  calDayText: { fontSize: 13, color: '#333' },
  calDayTextDone: { color: '#fff', fontWeight: '700' },
  calDayTextToday: { color: '#C8102E', fontWeight: '700' },
  calDayFaded: { color: '#ccc' },
});
